import type { OrganizationWorkingHours } from '@form-factory/schema';

/**
 * Converts a PascalCase string to a kebab-case string.
 *
 * @example
 * const str = 'MyVariableName';
 * const kebab = pascalToKebab(str);
 *
 * -> kebab will be 'my-variable-name'
 */
export function pascalToKebab(str: string) {
  return str.replaceAll(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

const NANO_ID_CHARS =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * Generates a random ID with 8 characters. In following format:
 * '2k4j3h7k', you can customize the length by passing a different number.
 */
export function nanoId(length = 8) {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += NANO_ID_CHARS[Math.floor(Math.random() * NANO_ID_CHARS.length)];
  }

  return result;
}

/**
 * Chunks an array into smaller arrays of a given size.
 */
export function chunkArray<T>(arr: T[], size: number): T[][] {
  return arr.reduce<T[][]>(
    (acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]),
    [],
  );
}

/**
 * Extracts the file extension from a URI. The output includes
 * leading dot, e.g. `.txt`, `.md`, etc.
 */
export function extractFileExtension(uri: string): string | null {
  const extension = uri.split('.').pop();

  if (!extension) {
    return null;
  }

  return `.${extension.toLocaleLowerCase()}`;
}

/**
 * Converts http and https URLs to ws and wss URLs.
 * The path is preserved. It is usually used to generate
 * websocket endpoints from base API urls.
 */
export function getWsUrlFromHttpUrl(url: string) {
  const parsedUrl = new URL(url);

  // Make sure the url ends without a last slash
  return `${parsedUrl.protocol === 'https:' ? 'wss' : 'ws'}://${parsedUrl.host}${parsedUrl.pathname.replace(/\/$/, '')}`;
}

/**
 * Sorts an array of objects by the 'createdAt' property.
 */
export function sortMessagesChronologically<
  T extends { createdAt: string | Date },
>(a: T, b: T): number {
  const aDate =
    a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
  const bDate =
    b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);

  return aDate.getTime() - bDate.getTime();
}

/**
 * Checks if a time range is valid. The from and to string are
 * expected to be in ISO 8601 format.
 *
 * @example
 * const isValid = isTimeRangeValid('2024-03-15T10:00:00Z', '2024-03-15T12:00:00Z');
 *
 */
export function isTimeRangeValid(from: string, to: string) {
  const now = new Date();
  const fromDate = new Date(from);
  const toDate = new Date(to);

  return (
    new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      fromDate.getHours(),
      fromDate.getMinutes(),
    ) <
    new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      toDate.getHours(),
      toDate.getMinutes(),
    )
  );
}

/**
 * Calculate the percentile value from a sorted array of numbers.
 */
export function getPercentile(
  sortedValues: number[],
  percentile: number,
): number {
  if (sortedValues.length === 0) {
    return 0;
  }

  if (percentile <= 0) {
    return sortedValues[0];
  }

  if (percentile >= 100) {
    return sortedValues.at(-1) ?? 0;
  }

  // Calculate the index with possible decimal part
  const index = (percentile / 100) * (sortedValues.length - 1);

  // Get integer and decimal parts
  const lowerIndex = Math.floor(index);
  const fraction = index - lowerIndex;

  // If it's exactly at an index
  if (fraction === 0) {
    return sortedValues[lowerIndex];
  }

  // Interpolate between the two closest values
  const lowerValue = sortedValues[lowerIndex];
  const upperValue = sortedValues[lowerIndex + 1];

  return lowerValue + (upperValue - lowerValue) * fraction;
}

/**
 * Converts ArrayBuffer to base64 string for web environments.
 * This is the most efficient native way to handle the conversion.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCodePoint(bytes[i]);
  }

  return btoa(binary);
}

export function isWithinHours(workingHours: OrganizationWorkingHours) {
  if (!workingHours) {
    return false;
  }

  const now = new Date();
  const workingHoursDay =
    workingHours[`${now.getDay()}` as keyof typeof workingHours];

  if (workingHoursDay && Array.isArray(workingHoursDay)) {
    for (const hours of workingHoursDay) {
      const from = new Date(hours.from);
      const to = new Date(hours.to);

      /**
       * The `from` and `to` dates are in the past, so we need to
       * convert them to the current date to simplify the comparison.
       * We are only interested in hours and minutes.
       */
      if (
        now >=
          new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            from.getHours(),
            from.getMinutes(),
          ) &&
        now <=
          new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            to.getHours(),
            to.getMinutes(),
          )
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Converts the hex color to HSL.
 *
 * #000000 -> [0, 0, 0]
 */
export function hexToHsl(hex: string) {
  const result = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);

  if (!result || result.length !== 4) {
    throw 'Failed to parse hex';
  }

  let r = Number.parseInt(result[1], 16);
  let g = Number.parseInt(result[2], 16);
  let b = Number.parseInt(result[3], 16);
  ((r /= 255), (g /= 255), (b /= 255));

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h = (max + min) / 2;
  let s = 0;
  let l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return [h, s, l];
}
